import { middleEllipsisString, uuid } from "@/app/infrastructures/misc/Utils";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import jsPDF from "jspdf";
import { logoBlack } from "../../constants/images";
// import { ResiWoodpackingDisplay } from "../../shared/resi-woodpacking-display";

const isBossPackOrSameDay = (data: any) => {
  const validTypes = ["BOSSPACK", "SAMEDAY", "VIPPACK"];
  return validTypes.includes(data);
};

const getPackAbbreviation = (fullName: string) => {
  const abbreviations: any = {
    BOSSPACK: "BOSS",
    REGPACK: "REG",
    JAGOPACK: "JAGO",
    ONEPACK: "ONE",
    INTERPACK: "INTER",
    BIGPACK: "BIG",
    JUMBOPACK: "JUMBO",
    SAMEDAY: "SAME",
    OTOPACK150: "OTO15",
    OTOPACK250: "OTO25",
    VIPPACK: "VIP",
    INTERPACKMAXI: "MAXI",
    BIGPACKFAST: "BIGF",
    MINIPACK: "MINI"
  };

  return abbreviations[fullName] || fullName;
};

const resolveFontStyle = (fontName: string, style: string) => {
  if (fontName === "Roboto Mono" && style === "extrabold") return "bold";
  if (fontName === "Inter" && style === "normal") return "medium";
  return style;
};

const setFontAuto = (doc: jsPDF, text: string, style: string) => {
  const hasDigit = /[0-9]/.test(text);
  const fontName = hasDigit ? "Roboto Mono" : "Inter";
  doc.setFont(fontName, resolveFontStyle(fontName, style));
};

const shiftX = (x: number) => x - 2;

const BASE_BADGE_FONT_SIZE = 15;

const getProductBadgeBox = (
  params: any,
  isVIPPack: boolean,
  productType: string,
  fontSize = BASE_BADGE_FONT_SIZE
): { x: number; width: number } | null => {
  if (!isBossPackOrSameDay(productType)) return null;

  const isInterMaxiType = productType === "INTERPACKMAXI";
  const baseWidth = isVIPPack ? 12 : isInterMaxiType ? 18.5 : 19;
  const width = baseWidth * (fontSize / BASE_BADGE_FONT_SIZE);
  const startXMargin = isVIPPack ? 64 : isInterMaxiType ? 57.5 : 57;
  // sisi kanan badge tetap di tempat, hanya lebarnya yang mengecil saat shrink-to-fit
  return {
    x: shiftX(params.startX + startXMargin) + (baseWidth - width),
    width
  };
};

interface BadgeConfig {
  doc: jsPDF;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
}

const renderBadge = (config: BadgeConfig) => {
  const fontSize = config.fontSize ?? BASE_BADGE_FONT_SIZE;
  config.doc.setLineWidth(0.3);
  config.doc.setFillColor("#333333");
  config.doc.setDrawColor("#333333");
  config.doc.roundedRect(
    config.x,
    config.y,
    config.width,
    config.height,
    0.5,
    0.5,
    "FD"
  );
  setFontAuto(config.doc, config.text, "bold");
  config.doc.setTextColor("#FFFFFF");
  config.doc.setFontSize(fontSize);
  // baseline teks di dalam badge: offset tetap di font 15, proporsional di bawahnya
  const textY =
    fontSize >= BASE_BADGE_FONT_SIZE
      ? config.y + 6
      : config.y + config.height / 2 + (fontSize * 0.3528 * 0.72) / 2;
  config.doc.text(
    config.text,
    config.x + fontSize / BASE_BADGE_FONT_SIZE,
    textY
  );
};

const renderProductType = (
  params: any,
  isVIPPack: boolean,
  abbreviationX: number,
  fontSize = BASE_BADGE_FONT_SIZE
) => {
  const productType = params.data?.sttProductType || "";

  const badgeBox = getProductBadgeBox(params, isVIPPack, productType, fontSize);
  if (badgeBox) {
    params.doc.roundedRect(
      badgeBox.x,
      params.startY + 3,
      badgeBox.width,
      8,
      0.5,
      0.5,
      "FD"
    );
  }

  const abbreviation = getPackAbbreviation(productType);
  setFontAuto(params.doc, abbreviation, "extrabold");
  params.doc.setTextColor(
    isBossPackOrSameDay(productType) ? "#FFFFFF" : "#333333"
  );
  params.doc.setFontSize(fontSize);
  const textY =
    fontSize >= BASE_BADGE_FONT_SIZE
      ? params.startY + 9
      : params.startY + 7 + (fontSize * 0.3528 * 0.72) / 2;
  params.doc.text(abbreviation, abbreviationX, textY, {
    align: "right"
  });
};

const renderStatusBadges = (
  params: any,
  abbreviationX: number,
  abbreviationWidth: number,
  productBadgeLeftX?: number,
  fontSize = BASE_BADGE_FONT_SIZE
) => {
  const badgeGap = 1.5;
  const scale = fontSize / BASE_BADGE_FONT_SIZE;
  let cursorRight = productBadgeLeftX
    ? productBadgeLeftX - badgeGap
    : abbreviationX - abbreviationWidth - badgeGap;

  const placeBadge = (width: number) => {
    const x = cursorRight - width;
    cursorRight = x - badgeGap;
    return x;
  };

  if (params.data?.sttCommodityIsQuarantine) {
    renderBadge({
      doc: params.doc,
      text: "K",
      x: placeBadge(6 * scale),
      y: params.startY + 3,
      width: 6 * scale,
      height: 8,
      fontSize
    });
  }

  if (params.data?.isDangerousGoods) {
    renderBadge({
      doc: params.doc,
      text: "DG",
      x: placeBadge(10 * scale),
      y: params.startY + 3,
      width: 10 * scale,
      height: 8,
      fontSize
    });
  }

  // badge PK dipanggil paling akhir supaya menempel paling kiri (dekat logo),
  // karena placeBadge() berjalan dari kanan ke kiri
  // const showPkBadge =
  //   params.woodpackingDisplay?.showKayuBadge ?? params.data?.sttIsWoodpacking;
  // if (showPkBadge) {
  //   const pkWidth =
  //     params.doc.getTextDimensions("PK", { fontSize }).w + 2 * scale;
  //   renderBadge({
  //     doc: params.doc,
  //     text: "PK",
  //     x: placeBadge(pkWidth),
  //     y: params.startY + 3,
  //     width: pkWidth,
  //     height: 8,
  //     fontSize
  //   });
  // }
};

const renderLogo = (params: any, logoAspectRatio: number) => {
  params.doc.addImage(
    logoBlack,
    "PNG",
    shiftX(params.startX + 5),
    params.startY + 3,
    32,
    logoAspectRatio * 32,
    "logo",
    "FAST",
    0
  );
};

const renderBarcode = (params: any) => {
  if (params.barcode) {
    params.doc.addImage(
      params.barcode,
      "PNG",
      shiftX(params.startX + 5),
      params.startY + 22,
      69,
      12,
      uuid(),
      "FAST",
      0
    );
  }

  const barcodeValue = String(params.barcodeValue || "");
  setFontAuto(params.doc, barcodeValue, "extrabold");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(18);
  params.doc.text(barcodeValue, shiftX(params.startX + 5), params.startY + 22);
};

const renderReference = (params: any, translate: Function) => {
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(8);
  params.doc.setFont("Inter", "medium");
  const translatedRef = String(translate("receipt.ref"));
  const refLabel = translatedRef === "receipt.ref" ? "Ref" : translatedRef;
  params.doc.text(
    `${refLabel}: `,
    shiftX(params.startX + 5),
    params.startY + 42
  );

  const refValue = params.data?.sttNoRefExternal
    ? middleEllipsisString(params.data.sttNoRefExternal)
    : "-";
  setFontAuto(params.doc, refValue, "bold");
  params.doc.text(refValue, shiftX(params.startX + 11), params.startY + 42);
};

const renderClientName = (params: any) => {
  const shipmentValue = String(
    params.data?.bookingId || params.shipmentId || ""
  );
  const clientName = String(params.data?.sttClientName || "");
  const maxLength = shipmentValue ? 35 : 40;
  const elipsisClientName =
    clientName.length > maxLength
      ? `${clientName.substring(0, maxLength)}...`
      : clientName;
  setFontAuto(params.doc, elipsisClientName, "bold");
  params.doc.setFontSize(6);
  params.doc.text(
    elipsisClientName,
    shiftX(params.startX + 5),
    params.startY + 45
  );
};

const renderLines = (params: any) => {
  // line after logo and client name
  params.doc.setLineWidth(0.4);
  params.doc.line(
    shiftX(params.startX),
    params.startY + 48,
    shiftX(params.startX + 108),
    params.startY + 48
  );
  params.doc.line(
    shiftX(params.startX + 77),
    params.startY + 48,
    shiftX(params.startX + 77),
    params.startY
  );
};

const renderShipmentId = (params: any) => {
  const shipmentValue = String(
    params.data?.bookingId || params.shipmentId || ""
  );
  setFontAuto(params.doc, shipmentValue, "bold");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(8);
  params.doc.text(
    shipmentValue,
    shiftX(params.startX + 73),
    params.startY + 45,
    "right"
  );
};

const calculateAbbreviationX = (params: any, isVIPPack: boolean) => {
  const productType = params.data?.sttProductType || "";
  const abbreviation = getPackAbbreviation(productType);
  const x =
    abbreviation.length > 4
      ? shiftX(params.startX + 76)
      : shiftX(params.startX + 74);
  return isVIPPack ? x + 1 : x;
};

export const headerSectionV3 = async (
  params: {
    doc: jsPDF | any;
    bookingType: string;
    accountType: string;
    data: PrintDataEntitas;
    pieceData: any;
    barcode: string;
    vendorRefCode: string;
    barcodeValue: string;
    vendorRefCodeValue: string;
    logo: string;
    shipmentId: string;
    startX: number;
    startY: number;
    // woodpackingDisplay?: ResiWoodpackingDisplay;
  },
  logoAspectRatio: number,
  translate: Function
) => {
  renderLogo(params, logoAspectRatio);

  const productType = params.data?.sttProductType || "";
  const isVIPPack = productType === "VIPPACK";
  // const showPkBadge =
  //   params.woodpackingDisplay?.showKayuBadge ?? params.data?.sttIsWoodpacking;

  // shrink-to-fit: saat badge PK aktif, badge (PK/K/DG) + product harus muat
  // dalam satu baris antara logo (startX+5, lebar 32) dan garis separator vertikal (startX+77)
  const badgeFontSize = BASE_BADGE_FONT_SIZE;
  // if (showPkBadge) {
  //   const logoRightX = shiftX(params.startX + 5) + 32 + 1;
  //   const baseProductBadgeBox = getProductBadgeBox(
  //     params,
  //     isVIPPack,
  //     productType
  //   );
  //   const productRightX = baseProductBadgeBox
  //     ? baseProductBadgeBox.x + baseProductBadgeBox.width
  //     : calculateAbbreviationX(params, isVIPPack);
  //   const statusTexts: string[] = [];
  //   if (params.data?.sttCommodityIsQuarantine) statusTexts.push("K");
  //   if (params.data?.isDangerousGoods) statusTexts.push("DG");
  //   statusTexts.push("PK");
  //   const badgeGap = 1.5;
  //   const abbreviation = getPackAbbreviation(productType);
  //
  //   while (badgeFontSize > 8) {
  //     const scale = badgeFontSize / BASE_BADGE_FONT_SIZE;
  //     const statusBadgesWidth = statusTexts.reduce(
  //       (sum, text) =>
  //         sum +
  //         (text === "PK"
  //           ? params.doc.getTextDimensions("PK", { fontSize: badgeFontSize })
  //               .w +
  //             2 * scale
  //           : (text === "K" ? 6 : 10) * scale),
  //       0
  //     );
  //     const productWidth = baseProductBadgeBox
  //       ? baseProductBadgeBox.width * scale
  //       : params.doc.getTextDimensions(abbreviation, {
  //           fontSize: badgeFontSize
  //         }).w;
  //     const rowLeftX =
  //       productRightX -
  //       productWidth -
  //       statusBadgesWidth -
  //       statusTexts.length * badgeGap;
  //     if (rowLeftX >= logoRightX) break;
  //     badgeFontSize -= 0.5;
  //   }
  // }

  const abbreviationX = calculateAbbreviationX(params, isVIPPack);
  const productBadgeBox = getProductBadgeBox(
    params,
    isVIPPack,
    productType,
    badgeFontSize
  );

  params.doc.setLineWidth(0.3);
  params.doc.setFillColor("#333333");
  params.doc.setDrawColor("#333333");

  renderProductType(params, isVIPPack, abbreviationX, badgeFontSize);

  const abbreviationWidth = params.doc.getTextDimensions(
    getPackAbbreviation(productType),
    { fontSize: badgeFontSize }
  ).w;
  renderStatusBadges(
    params,
    abbreviationX,
    abbreviationWidth,
    productBadgeBox?.x,
    badgeFontSize
  );

  renderBarcode(params);
  renderReference(params, translate);
  renderClientName(params);
  renderLines(params);
  renderShipmentId(params);

  return {
    ...params,
    startX: 77,
    startY: 40
  };
};
